// src/pages/SalesCockpit.jsx
// CRM Call Cockpit: the NocoDB CRM (crm.baldin.io) is the database, calling
// happens here. Left pane = filterable call list or map, right pane = the
// selected company, tabbed:
//   Anruf  — outcome buttons, timestamped call log, follow-up with ICS export
//   Teaser — search profile (generatable from the company website via the
//            same crawl+LLM mechanism as TED query generation), tender
//            pre-search with hand-picked selection, one-click teaser mail
// Header shows the live Anruf-Timing window and which Bundesländer are in
// Schulferien (Taktik-Board data).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useGraphQL } from '../graphql';
import ActionFeedback from '../components/common/ActionFeedback';
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    CalendarDaysIcon,
    GlobeEuropeAfricaIcon,
    ListBulletIcon,
    MapPinIcon,
    PaperAirplaneIcon,
    PhoneIcon,
    SparklesIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {
    CALL_RULES,
    QUALITY_META,
    activeVacations,
    currentCallWindow,
    vacationStatus,
} from '../data/callPlanning';

const STATUS_META = {
    'Recherchiert': { badge: 'bg-slate-100 text-slate-600' },
    'Nicht erreicht': { badge: 'bg-amber-100 text-amber-800' },
    'Rückruf': { badge: 'bg-blue-100 text-blue-700' },
    'Interessiert': { badge: 'bg-emerald-100 text-emerald-700' },
    'Termin': { badge: 'bg-purple-100 text-purple-700' },
    'Kein Interesse': { badge: 'bg-red-100 text-red-700' },
    'Ungültig': { badge: 'bg-slate-200 text-slate-400' },
};
const OUTCOME_ORDER = ['Nicht erreicht', 'Rückruf', 'Interessiert', 'Termin', 'Kein Interesse', 'Ungültig'];

// Founder-meeting slots render in spoken form ("Mi., 27.08. 10:30") — the
// exact words the Telefonkarte's alternative question needs.
const SLOT_DAY_FMT = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' });
const SLOT_TIME_FMT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const fmtSlot = (iso) => `${SLOT_DAY_FMT.format(new Date(iso))} ${SLOT_TIME_FMT.format(new Date(iso))}`;
const formatHelloCode = (code) => (code && code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code || '');
const slotDayKey = (iso) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Berlin' }).format(new Date(iso));
// The alternative question wants two options on different days.
const nextTwoAlternatives = (slots) => {
    if (slots.length === 0) return [];
    const first = slots[0];
    const other = slots.find((s) => slotDayKey(s.start) !== slotDayKey(first.start));
    return other ? [first, other] : [first];
};

const PRIO_META = {
    A: { badge: 'bg-secondary text-white', marker: '#FF0092' },
    B: { badge: 'bg-primary text-on-accent', marker: '#00BB8F' },
    C: { badge: 'bg-slate-200 text-slate-600', marker: '#94a3b8' },
};

const CPV_PRESETS = [
    ['71000000', 'PLANUNG'],
    ['45000000', 'BAU'],
    ['72000000', 'IT'],
    ['90910000', 'REINIGUNG'],
    ['79993100', 'FACILITY'],
];
const NUTS_PRESETS = [
    ['DED', 'SACHSEN'],
    ['DED2', 'DRESDEN'],
    ['DE3', 'BERLIN'],
    ['DEE', 'S-ANHALT'],
    ['DEG', 'THÜRINGEN'],
];
const MATCH_LIMITS = [10, 25];
const DEFAULT_SEND_COUNT = 5;

const formatValue = (value, currency) => {
    if (!value) return '';
    return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value)} ${currency || 'EUR'}`;
};

const inputClass = 'w-full bg-gray-700 border border-gray-600 text-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
const labelClass = 'block font-mono text-xs text-gray-500 uppercase mb-1';
const primaryBtn = 'px-4 py-2 rounded-lg font-mono text-xs font-bold text-on-accent bg-primary hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed uppercase transition-all duration-150';
const secondaryBtn = 'px-3 py-2 rounded-lg font-mono text-xs font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 uppercase transition-all duration-150';

const parseCrmDate = (value) => {
    if (!value) return null;
    const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatCrmDateTime = (value) => {
    const d = parseCrmDate(value);
    if (!d) return '';
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const toDatetimeLocal = (value) => {
    const d = parseCrmDate(value);
    if (!d) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const telHref = (phone) => `tel:${(phone || '').replace(/[^\d+]/g, '')}`;

// ── ICS export ───────────────────────────────────────────────────────────────

const icsEscape = (s) => (s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
const icsUtc = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

const buildIcs = (events) => {
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//BidLyzer//Call Cockpit//DE'];
    events.forEach((company) => {
        const start = parseCrmDate(company.followUpAt);
        if (!start) return;
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        const description = [
            company.phone && `Telefon: ${company.phone}`,
            company.people?.length && `Ansprechpartner: ${company.people.map((p) => p.name).join(', ')}`,
            (company.notes || '').split('\n')[0],
        ].filter(Boolean).join('\n');
        lines.push(
            'BEGIN:VEVENT',
            `UID:crm-followup-${company.id}@bidlyzer.com`,
            `DTSTAMP:${icsUtc(new Date())}`,
            `DTSTART:${icsUtc(start)}`,
            `DTEND:${icsUtc(end)}`,
            `SUMMARY:${icsEscape(`Rückruf: ${company.name}`)}`,
            `DESCRIPTION:${icsEscape(description)}`,
            `LOCATION:${icsEscape([company.city, company.state].filter(Boolean).join(', '))}`,
            'END:VEVENT',
        );
    });
    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
};

const downloadIcs = (filename, events) => {
    const blob = new Blob([buildIcs(events)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

// ── small presentational pieces ──────────────────────────────────────────────

const PresetChips = ({ presets, onPick }) => (
    <div className="flex flex-wrap gap-1 mt-1">
        {presets.map(([value, label]) => (
            <button key={value} type="button" onClick={() => onPick(value)} title={value}
                className="px-1.5 py-0.5 rounded-md font-mono text-[10px] text-slate-600 bg-white border border-slate-200 hover:border-primary hover:text-primary uppercase transition-all duration-150">
                {label}
            </button>
        ))}
    </div>
);

const Chip = ({ className, children, title }) => (
    <span title={title} className={`font-mono font-bold uppercase rounded-md text-[10px] px-1.5 py-0.5 ${className}`}>{children}</span>
);

const CallWindowBadge = ({ now }) => {
    const window = currentCallWindow(now);
    const meta = QUALITY_META[window.quality];
    return (
        <span className={`font-mono text-xs font-bold uppercase rounded-md px-2 py-1 ${meta.badge}`}
            title={CALL_RULES.join('\n')}>
            {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} · {meta.label}
            {window.until ? ` bis ${window.until}` : ''}
            {window.note ? ` (${window.note})` : ''}
        </span>
    );
};

const VacationBanner = ({ now }) => {
    const active = activeVacations(now);
    if (active.length === 0) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span className="font-mono uppercase font-bold text-amber-700">Ferien:</span>
            {active.map(({ code, name, status }) => (
                <Chip key={code} className="bg-amber-100 text-amber-800"
                    title={`${name}: ${status.label} bis ${status.until}`}>
                    {code} bis {status.until.slice(8, 10)}.{status.until.slice(5, 7)}.
                </Chip>
            ))}
            <span>— Erreichbarkeit kleiner Büros reduziert</span>
        </div>
    );
};

// ── page ─────────────────────────────────────────────────────────────────────

const SalesCockpit = () => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState(null);
    const [now, setNow] = useState(() => new Date());

    const [search, setSearch] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [prioFilter, setPrioFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [followUpOnly, setFollowUpOnly] = useState(false);
    const [view, setView] = useState('list');

    const [selectedId, setSelectedId] = useState(null);
    const [pendingStatus, setPendingStatus] = useState('');
    const [note, setNote] = useState('');
    const [followUpInput, setFollowUpInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [geocoding, setGeocoding] = useState(null);

    // Founder-meeting slots (Profil-Check): the same live availability the
    // prospect pages offer. Loaded once + on demand — the header shows the
    // next two options so the agent can say "Morgen 10:30 oder Do 14:00?"
    // without leaving the call.
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [bookingSlot, setBookingSlot] = useState('');
    const [bookingName, setBookingName] = useState('');
    const [bookingEmail, setBookingEmail] = useState('');
    const [booking, setBooking] = useState(false);
    const [bookedMeeting, setBookedMeeting] = useState(null);
    const [sendingHello, setSendingHello] = useState(false);

    const loadSlots = useCallback(async () => {
        setSlotsLoading(true);
        try {
            const data = await executeQuery(queries.getMeetingSlots(), { kind: 'PROFIL_CHECK' });
            setSlots(data.meetingSlots || []);
        } catch (err) {
            console.error('meetingSlots failed', err);
        } finally {
            setSlotsLoading(false);
        }
    }, [executeQuery, queries]);
    useEffect(() => { loadSlots(); }, [loadSlots]);

    // Teaser & tender matching: the SalesProspect row (bidlyzer DB) linked to
    // the selected CRM company via crmRecordId carries the search profile and
    // the teaser-mail machinery. Companies without one get an activate button.
    const [prospectsByCrm, setProspectsByCrm] = useState({});
    const [prospectDetail, setProspectDetail] = useState(null);
    const [profileForm, setProfileForm] = useState({ query: '', cpvCode: '', nutsCodePrefix: '' });
    const [matches, setMatches] = useState([]);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [matchesError, setMatchesError] = useState('');
    const [matchLimit, setMatchLimit] = useState(MATCH_LIMITS[0]);
    const [checkedTenders, setCheckedTenders] = useState(new Set());
    const [recipientDraft, setRecipientDraft] = useState('');
    const [subjectDraft, setSubjectDraft] = useState('');
    const [informal, setInformal] = useState(false);
    const [personalNote, setPersonalNote] = useState('');
    const [includeInsight, setIncludeInsight] = useState(true);
    const [sending, setSending] = useState(false);
    const [activating, setActivating] = useState(false);
    // Website → profile generation (crawl + LLM, same mechanism as the TED
    // queries for account companies). suggestion keeps the generated term and
    // code pools for the refinement chips.
    const [generating, setGenerating] = useState(false);
    const [suggestion, setSuggestion] = useState(null);
    // The detail pane is tabbed; the tab is sticky across company switches so
    // a teaser sweep through the list stays in teaser mode.
    const [detailTab, setDetailTab] = useState('call');
    // Guards teaser responses against fast company switching.
    const selectedIdRef = useRef(null);

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerLayer = useRef(null);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    const loadCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const data = await executeQuery(queries.getCrmCallList(), { limit: 2000 });
            setCompanies(data.crmCallList.items);
        } catch (err) {
            setFeedback({ isError: true, message: `Laden fehlgeschlagen: ${err.message}` });
        } finally {
            setLoading(false);
        }
    }, [executeQuery, queries]);

    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const facets = useMemo(() => {
        const collect = (key) => [...new Set(companies.map((c) => c[key]).filter(Boolean))].sort();
        return { states: collect('state'), statuses: collect('status'), sources: collect('source') };
    }, [companies]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = companies.filter((c) =>
            (!stateFilter || c.state === stateFilter) &&
            (!prioFilter || c.prio === prioFilter) &&
            (!statusFilter || (statusFilter === '—' ? !c.status : c.status === statusFilter)) &&
            (!sourceFilter || c.source === sourceFilter) &&
            (!followUpOnly || c.followUpAt) &&
            (!q || [c.name, c.city, c.phone, c.notes, c.categories, ...(c.people || []).map((p) => p.name)]
                .some((v) => v && v.toLowerCase().includes(q))));
        if (followUpOnly) {
            list = [...list].sort((a, b) => (a.followUpAt || '').localeCompare(b.followUpAt || ''));
        }
        return list;
    }, [companies, search, stateFilter, prioFilter, statusFilter, sourceFilter, followUpOnly]);

    const selected = useMemo(() => companies.find((c) => c.id === selectedId) || null, [companies, selectedId]);
    const followUps = useMemo(() => companies.filter((c) => c.followUpAt), [companies]);
    const missingGeo = useMemo(() => companies.filter((c) => c.lat == null).length, [companies]);

    const loadProspects = useCallback(async () => {
        try {
            const data = await executeQuery(queries.getSalesProspects(), { limit: 2000, offset: 0 });
            const map = {};
            (data.salesProspects?.items || []).forEach((p) => {
                if (p.crmRecordId) map[p.crmRecordId] = p;
            });
            setProspectsByCrm(map);
            return map;
        } catch {
            return prospectsByCrm; // teaser section falls back to the activate button
        }
    }, [executeQuery, queries]);

    useEffect(() => { loadProspects(); }, [loadProspects]);

    const loadMatches = useCallback(async (prospectId, companyId, limit = matchLimit) => {
        setMatchesLoading(true);
        setMatchesError('');
        try {
            const data = await executeQuery(queries.getSalesProspectMatches(), { id: prospectId, limit });
            if (selectedIdRef.current !== companyId) return;
            const found = data.salesProspectMatches || [];
            setMatches(found);
            setCheckedTenders(new Set(found.slice(0, DEFAULT_SEND_COUNT).map((t) => t.id)));
        } catch (err) {
            if (selectedIdRef.current !== companyId) return;
            setMatches([]);
            setCheckedTenders(new Set());
            setMatchesError(err.message);
        } finally {
            if (selectedIdRef.current === companyId) setMatchesLoading(false);
        }
    }, [executeQuery, queries, matchLimit]);

    const loadProspectDetail = useCallback(async (prospectId, companyId) => {
        try {
            const data = await executeQuery(queries.getSalesProspect(), { id: prospectId });
            if (selectedIdRef.current === companyId && data.salesProspect) {
                setProspectDetail(data.salesProspect);
            }
        } catch {
            // email history stays empty — not worth a banner
        }
    }, [executeQuery, queries]);

    const selectCompany = (company) => {
        selectedIdRef.current = company.id;
        setSelectedId(company.id);
        setPendingStatus('');
        setNote('');
        setFollowUpInput(toDatetimeLocal(company.followUpAt));

        const prospect = prospectsByCrm[company.id];
        setProspectDetail(null);
        setMatches([]);
        setCheckedTenders(new Set());
        setMatchesError('');
        setPersonalNote('');
        setIncludeInsight(true);
        setSuggestion(null);
        const profile = prospect?.searchProfile || {};
        setProfileForm({
            query: profile.query || '',
            cpvCode: profile.cpvCode || '',
            nutsCodePrefix: profile.nutsCodePrefix || '',
        });
        setRecipientDraft(prospect?.contactEmail || '');
        setBookedMeeting(null);
        setBookingSlot('');
        setBookingName(prospect?.contactName || company.people?.[0]?.name || '');
        setBookingEmail(prospect?.contactEmail || '');
        if (prospect) {
            loadProspectDetail(prospect.id, company.id);
            if (prospect.searchProfile) loadMatches(prospect.id, company.id);
        }
    };

    // ensureProspect: resolve the SalesProspect for the selected CRM company,
    // creating (activating) it when missing. Shared by the teaser tab and the
    // in-call Termin booking. Throws with a user-readable message on failure.
    const ensureProspect = async () => {
        if (!selected) throw new Error('Keine Firma ausgewählt');
        const existing = prospectsByCrm[selected.id];
        if (existing) return existing;
        const row = {
            companyName: selected.name,
            contactName: selected.people?.[0]?.name || null,
            phone: selected.phone || null,
            city: selected.city || null,
            segment: selected.categories || null,
            website: selected.domains ? selected.domains.split(',')[0].trim() : null,
            crmRecordId: selected.id,
        };
        const result = await executeMutation(mutations.importSalesProspects, {
            input: { listName: 'Cockpit', rows: [row] },
        });
        const payload = result.importSalesProspects;
        if (!payload.success) throw new Error(payload.error || 'Aktivierung fehlgeschlagen');
        if (payload.created === 0) {
            throw new Error(`Prospect existiert bereits ohne CRM-Verknüpfung (${(payload.skippedNames || []).join(', ') || selected.name}) — im alten Datenbestand prüfen`);
        }
        const map = await loadProspects();
        const prospect = map[selected.id];
        if (!prospect) throw new Error('Prospect nach Aktivierung nicht auffindbar');
        return prospect;
    };

    const activateTeaser = async () => {
        if (!selected) return;
        setActivating(true);
        try {
            const prospect = await ensureProspect();
            setFeedback({ message: `${selected.name} für Teaser aktiviert` });
            loadProspectDetail(prospect.id, selected.id);
        } catch (err) {
            setFeedback({ isError: true, message: `Aktivierung fehlgeschlagen: ${err.message}` });
        } finally {
            setActivating(false);
        }
    };

    // Book the Profil-Check while the prospect is on the phone. The backend
    // sends both mails, flips the prospect to PROFILE_CHECK and pushes
    // Status "Termin" + follow-up into the CRM — one click, loop closed.
    const bookMeeting = async () => {
        if (!bookingSlot || !bookingEmail.trim()) return;
        setBooking(true);
        try {
            const prospect = await ensureProspect();
            const result = await executeMutation(mutations.adminBookMeeting, {
                input: {
                    kind: 'PROFIL_CHECK',
                    startAt: bookingSlot,
                    prospectId: prospect.id,
                    name: bookingName.trim() || prospect.contactName || selected.name,
                    email: bookingEmail.trim(),
                },
            });
            const payload = result.adminBookMeeting;
            if (!payload.success) {
                setFeedback({ isError: true, message: payload.error || 'Buchung fehlgeschlagen' });
                if ((payload.error || '').includes('nicht mehr verfügbar')) loadSlots();
                return;
            }
            setBookedMeeting(payload.meeting);
            setBookingSlot('');
            mergeCompany({ ...selected, status: 'Termin', followUpAt: payload.meeting.startAt });
            setPendingStatus('');
            setFeedback({ message: `Profil-Check gebucht: ${fmtSlot(payload.meeting.startAt)} — Bestätigung ist raus an ${bookingEmail.trim()}` });
            loadSlots();
        } catch (err) {
            setFeedback({ isError: true, message: `Buchung fehlgeschlagen: ${err.message}` });
        } finally {
            setBooking(false);
        }
    };

    // The first CRM domain doubles as the crawl target when the prospect row
    // has no website yet (older activations predate the website copy).
    // The onboarding trigger: hello-Einladung per Mail (Link + Code). For
    // phone-only contacts there is no mail — the agent reads the code aloud
    // (it is pinned next to the personal link in both tabs).
    const sendHelloInvite = async () => {
        setSendingHello(true);
        try {
            const prospect = await ensureProspect();
            const recipient = (bookingEmail || recipientDraft || prospect.contactEmail || '').trim();
            if (!recipient) {
                setFeedback({ isError: true, message: 'Keine E-Mail bekannt — im Call erfragen oder Code vorlesen (hello.bidlyzer.com)' });
                return;
            }
            const result = await executeMutation(mutations.sendSalesHelloInvite, {
                input: { prospectId: prospect.id, recipient },
            });
            const payload = result.sendSalesHelloInvite;
            if (!payload.success) {
                setFeedback({ isError: true, message: payload.error || 'hello-Einladung fehlgeschlagen' });
                return;
            }
            setFeedback({ message: `hello-Einladung an ${recipient} ist raus (Code ${formatHelloCode(prospect.helloCode)})` });
            loadProspects();
        } catch (err) {
            setFeedback({ isError: true, message: `hello-Einladung fehlgeschlagen: ${err.message}` });
        } finally {
            setSendingHello(false);
        }
    };

    const prospectWebsite = (prospect, company) =>
        prospect?.website || (company?.domains ? company.domains.split(',')[0].trim() : '');

    const generateProfile = async () => {
        const prospect = prospectsByCrm[selected?.id];
        if (!prospect) return;
        const companyId = selected.id;
        const website = prospectWebsite(prospect, selected);
        if (!website) {
            setFeedback({ isError: true, message: 'Keine Website bekannt — Domain im CRM pflegen' });
            return;
        }
        setGenerating(true);
        setSuggestion(null);
        try {
            if (!prospect.website) {
                await executeMutation(mutations.updateSalesProspect, { id: prospect.id, input: { website } });
            }
            // CRM knowledge sharpens the LLM prompt; the backend falls back to
            // the prospect snapshot when this is empty.
            const description = [selected.description, selected.categories && `Kategorien: ${selected.categories}`]
                .filter(Boolean).join('\n') || null;
            const result = await executeMutation(mutations.generateSalesProspectProfile, {
                id: prospect.id, description,
            });
            const payload = result.generateSalesProspectProfile;
            if (selectedIdRef.current !== companyId) return;
            if (!payload.success) {
                setFeedback({ isError: true, message: payload.error || 'Profil-Generierung fehlgeschlagen' });
                return;
            }
            const profile = payload.prospect?.searchProfile || {};
            setProfileForm({
                query: profile.query || '',
                cpvCode: profile.cpvCode || '',
                nutsCodePrefix: profile.nutsCodePrefix || '',
            });
            setSuggestion(payload);
            setFeedback({ message: `Suchprofil aus ${payload.websiteUrl || website} generiert` });
            await loadProspects();
            await loadMatches(prospect.id, companyId);
        } catch (err) {
            if (selectedIdRef.current === companyId) {
                setFeedback({ isError: true, message: `Profil-Generierung fehlgeschlagen: ${err.message}` });
            }
        } finally {
            setGenerating(false);
        }
    };

    const saveProfile = async () => {
        const prospect = prospectsByCrm[selected?.id];
        if (!prospect) return;
        try {
            const input = {
                searchProfile: {
                    query: profileForm.query || null,
                    cpvCode: profileForm.cpvCode || null,
                    nutsCodePrefix: profileForm.nutsCodePrefix || null,
                },
            };
            const result = await executeMutation(mutations.updateSalesProspect, { id: prospect.id, input });
            if (!result.updateSalesProspect.success) {
                setFeedback({ isError: true, message: result.updateSalesProspect.error || 'Profil speichern fehlgeschlagen' });
                return;
            }
            setFeedback({ message: 'Suchprofil gespeichert' });
            await loadProspects();
            await loadMatches(prospect.id, selected.id);
        } catch (err) {
            setFeedback({ isError: true, message: `Profil speichern fehlgeschlagen: ${err.message}` });
        }
    };

    const sendTeaser = async (force = false) => {
        const prospect = prospectsByCrm[selected?.id];
        if (!prospect) return;
        const tenderIds = matches.filter((t) => checkedTenders.has(t.id)).map((t) => t.id);
        if (tenderIds.length === 0) {
            setFeedback({ isError: true, message: 'Mindestens eine Ausschreibung auswählen' });
            return;
        }
        setSending(true);
        try {
            const result = await executeMutation(mutations.sendSalesTeaser, {
                input: {
                    prospectId: prospect.id,
                    tenderIds,
                    recipient: recipientDraft || null,
                    subject: subjectDraft || null,
                    informal: informal || null,
                    personalNote: personalNote || null,
                    force: force || null,
                    includeInsight,
                },
            });
            const payload = result.sendSalesTeaser;
            if (payload.success) {
                setFeedback({ message: `Teaser an ${payload.email?.recipient} gesendet (${tenderIds.length} Ausschreibungen)` });
                setPersonalNote('');
                setSubjectDraft('');
                await loadProspects();
                await loadProspectDetail(prospect.id, selected.id);
            } else if (!force && payload.error?.includes('use force')) {
                if (window.confirm(`${payload.error}\n\nTrotzdem erneut senden?`)) {
                    setSending(false);
                    await sendTeaser(true);
                    return;
                }
            } else {
                setFeedback({ isError: true, message: payload.error || 'Senden fehlgeschlagen' });
            }
        } catch (err) {
            setFeedback({ isError: true, message: `Senden fehlgeschlagen: ${err.message}` });
        } finally {
            setSending(false);
        }
    };

    const toggleTender = (id) => {
        setCheckedTenders((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const mergeCompany = (updated) => {
        setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    };

    const logCall = async ({ status, clearFollowUp = false } = {}) => {
        if (!selected) return;
        const input = { companyId: selected.id };
        const effectiveStatus = status ?? pendingStatus;
        if (effectiveStatus) input.status = effectiveStatus;
        if (note.trim()) input.note = note.trim();
        if (clearFollowUp) {
            input.clearFollowUp = true;
        } else if (followUpInput && followUpInput !== toDatetimeLocal(selected.followUpAt)) {
            input.followUpAt = followUpInput;
        }
        if (!input.status && !input.note && !input.followUpAt && !input.clearFollowUp) return;

        setSaving(true);
        try {
            const data = await executeMutation(mutations.crmLogCall, { input });
            mergeCompany(data.crmLogCall);
            setPendingStatus('');
            setNote('');
            setFollowUpInput(toDatetimeLocal(data.crmLogCall.followUpAt));
            setFeedback({ message: `${data.crmLogCall.name} aktualisiert` });
        } catch (err) {
            setFeedback({ isError: true, message: `Speichern fehlgeschlagen: ${err.message}` });
        } finally {
            setSaving(false);
        }
    };

    const runGeocoding = async () => {
        setGeocoding({ done: 0, failed: 0 });
        try {
            for (;;) {
                const data = await executeMutation(mutations.crmGeocodeCompanies, { limit: 50 });
                const result = data.crmGeocodeCompanies;
                setGeocoding((prev) => ({ done: prev.done + result.geocoded, failed: prev.failed + result.failed }));
                if (result.remaining <= 0 || result.geocoded === 0) break;
            }
            await loadCompanies();
        } catch (err) {
            setFeedback({ isError: true, message: `Geocoding fehlgeschlagen: ${err.message}` });
        } finally {
            setGeocoding(null);
        }
    };

    // Leaflet lifecycle: init once the map pane is shown, re-render markers on
    // filter changes. CircleMarkers avoid the bundler icon-asset dance.
    useEffect(() => {
        if (view !== 'map' || !mapRef.current) return;
        if (!mapInstance.current) {
            mapInstance.current = L.map(mapRef.current).setView([51.05, 13.74], 7);
            L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap',
            }).addTo(mapInstance.current);
            markerLayer.current = L.layerGroup().addTo(mapInstance.current);
        }
        const layer = markerLayer.current;
        layer.clearLayers();
        const points = filtered.filter((c) => c.lat != null && c.lng != null);
        points.forEach((company) => {
            const marker = L.circleMarker([company.lat, company.lng], {
                radius: company.prio === 'A' ? 7 : 5,
                color: (PRIO_META[company.prio] || PRIO_META.C).marker,
                fillOpacity: 0.75,
                weight: 1.5,
            });
            marker.bindTooltip(`${company.name}${company.city ? ` · ${company.city}` : ''}`);
            marker.on('click', () => selectCompany(company));
            layer.addLayer(marker);
        });
        if (points.length > 0) {
            mapInstance.current.fitBounds(L.latLngBounds(points.map((c) => [c.lat, c.lng])), { padding: [24, 24], maxZoom: 10 });
        }
        setTimeout(() => mapInstance.current?.invalidateSize(), 50);
    }, [view, filtered]);

    useEffect(() => () => {
        mapInstance.current?.remove();
        mapInstance.current = null;
    }, []);

    const overdue = (c) => {
        const d = parseCrmDate(c.followUpAt);
        return d && d < now;
    };

    return (
        <div className="p-6 max-w-[1600px] mx-auto">
            {feedback && <ActionFeedback feedback={feedback} onDismiss={() => setFeedback(null)} className="mb-4" />}

            <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold font-mono uppercase">Call Cockpit</h1>
                <span className="font-mono text-xs text-slate-500">{filtered.length} / {companies.length} Firmen</span>
                <CallWindowBadge now={now} />
                {/* The alternative question, ready to read out: next free
                    Profil-Check options on two different days. */}
                <span className="font-mono text-xs text-slate-600" title="Nächste freie Profil-Check-Slots (Buchung im Anruf-Tab über Ergebnis „Termin“)">
                    {slotsLoading ? 'Slots…' : nextTwoAlternatives(slots).length > 0
                        ? `Frei: ${nextTwoAlternatives(slots).map((s) => fmtSlot(s.start)).join(' · ')}`
                        : 'Keine freien Slots'}
                </span>
                <div className="flex-1" />
                <a href="https://crm.baldin.io" target="_blank" rel="noreferrer" className={secondaryBtn}>
                    CRM <ArrowTopRightOnSquareIcon className="w-3 h-3 inline" />
                </a>
                <button type="button" onClick={loadCompanies} className={secondaryBtn} disabled={loading}>
                    <ArrowPathIcon className={`w-4 h-4 inline ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>
            <div className="mb-4"><VacationBanner now={now} /></div>

            <div className="flex flex-wrap items-end gap-2 mb-4">
                <div className="w-56">
                    <label className={labelClass}>Suche</label>
                    <input className={inputClass} value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Name, Ort, Telefon, Notiz…" />
                </div>
                <div>
                    <label className={labelClass}>Land</label>
                    <select className={inputClass} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                        <option value="">alle</option>
                        {facets.states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Prio</label>
                    <select className={inputClass} value={prioFilter} onChange={(e) => setPrioFilter(e.target.value)}>
                        <option value="">alle</option>
                        {['A', 'B', 'C'].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Status</label>
                    <select className={inputClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">alle</option>
                        <option value="—">ohne Status</option>
                        {facets.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Liste</label>
                    <select className={inputClass} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                        <option value="">alle</option>
                        {facets.sources.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button type="button" onClick={() => setFollowUpOnly((v) => !v)}
                    className={`${secondaryBtn} ${followUpOnly ? 'ring-2 ring-primary' : ''}`}>
                    <CalendarDaysIcon className="w-4 h-4 inline mr-1" />Rückrufe ({followUps.length})
                </button>
                <button type="button" onClick={() => downloadIcs('bidlyzer-rueckrufe.ics', followUps)}
                    className={secondaryBtn} disabled={followUps.length === 0} title="Alle Rückrufe als Kalender-Datei">
                    <ArrowDownTrayIcon className="w-4 h-4 inline mr-1" />ICS
                </button>
                <div className="flex-1" />
                <div className="flex rounded-lg overflow-hidden border border-slate-200">
                    <button type="button" onClick={() => setView('list')}
                        className={`px-3 py-2 font-mono text-xs font-bold uppercase ${view === 'list' ? 'bg-primary text-on-accent' : 'bg-white text-slate-600'}`}>
                        <ListBulletIcon className="w-4 h-4 inline mr-1" />Liste
                    </button>
                    <button type="button" onClick={() => setView('map')}
                        className={`px-3 py-2 font-mono text-xs font-bold uppercase ${view === 'map' ? 'bg-primary text-on-accent' : 'bg-white text-slate-600'}`}>
                        <MapPinIcon className="w-4 h-4 inline mr-1" />Karte
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4">
                <div className="surface-card p-0 overflow-hidden">
                    {view === 'map' ? (
                        <div className="relative">
                            <div ref={mapRef} className="h-[640px] z-0" />
                            {missingGeo > 0 && (
                                <div className="absolute top-2 right-2 z-[1000]">
                                    <button type="button" onClick={runGeocoding} disabled={!!geocoding} className={primaryBtn}>
                                        <GlobeEuropeAfricaIcon className="w-4 h-4 inline mr-1" />
                                        {geocoding
                                            ? `Geocoding… ${geocoding.done} ok / ${geocoding.failed} fehlgeschlagen`
                                            : `${missingGeo} ohne Koordinaten — geocodieren`}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="max-h-[640px] overflow-y-auto divide-y divide-slate-100">
                            {loading && <div className="p-6 font-mono text-xs text-slate-400 uppercase">Lade CRM…</div>}
                            {!loading && filtered.length === 0 && (
                                <div className="p-6 font-mono text-xs text-slate-400 uppercase">Keine Treffer</div>
                            )}
                            {filtered.map((company) => {
                                const vacation = company.state ? vacationStatus(company.state, now) : null;
                                return (
                                    <button type="button" key={company.id} onClick={() => selectCompany(company)}
                                        className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors ${selectedId === company.id ? 'bg-primary/5 border-l-4 border-primary' : 'border-l-4 border-transparent'}`}>
                                        <div className="flex items-center gap-2">
                                            <Chip className={(PRIO_META[company.prio] || PRIO_META.C).badge}>{company.prio || '·'}</Chip>
                                            <span className="font-semibold text-sm truncate">{company.name}</span>
                                            {company.status && (
                                                <Chip className={(STATUS_META[company.status] || STATUS_META.Recherchiert).badge}>{company.status}</Chip>
                                            )}
                                            {company.followUpAt && (
                                                <Chip className={overdue(company) ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-600'}
                                                    title={`Rückruf ${formatCrmDateTime(company.followUpAt)}`}>
                                                    ⏰ {formatCrmDateTime(company.followUpAt)}
                                                </Chip>
                                            )}
                                            <div className="flex-1" />
                                            {vacation?.active && (
                                                <Chip className="bg-amber-100 text-amber-800" title={`${company.state}: ${vacation.label} bis ${vacation.until}`}>F</Chip>
                                            )}
                                            <span className="font-mono text-xs text-slate-400">{company.city}{company.state ? ` · ${company.state}` : ''}</span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 pl-8">
                                            {company.phone && <span className="font-mono text-xs text-slate-500">{company.phone}</span>}
                                            {company.people?.length > 0 && (
                                                <span className="text-xs text-slate-400 truncate">{company.people.map((p) => p.name).join(', ')}</span>
                                            )}
                                            {company.categories && <span className="text-xs text-slate-300 truncate">{company.categories.split(',').join(' · ')}</span>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="surface-card p-4 h-fit sticky top-4">
                    {!selected ? (
                        <div className="font-mono text-xs text-slate-400 uppercase py-16 text-center">
                            Firma auswählen — Anruf-Notizen, Status und Rückrufe landen direkt im CRM
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-start gap-2">
                                    <h2 className="text-lg font-bold flex-1">{selected.name}</h2>
                                    <Chip className={(PRIO_META[selected.prio] || PRIO_META.C).badge}>{selected.prio || '·'}</Chip>
                                    {selected.status && (
                                        <Chip className={(STATUS_META[selected.status] || STATUS_META.Recherchiert).badge}>{selected.status}</Chip>
                                    )}
                                </div>
                                <div className="font-mono text-xs text-slate-500 mt-1">
                                    {[selected.address || selected.city, selected.state, selected.categories && selected.categories.split(',').join(' / '), selected.size, selected.employees && `${selected.employees} MA`]
                                        .filter(Boolean).join(' · ')}
                                </div>
                                <div className="font-mono text-[10px] text-slate-400 mt-0.5">
                                    {[selected.source, selected.lastCallAt && `zuletzt ${formatCrmDateTime(selected.lastCallAt)}`].filter(Boolean).join(' · ')}
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {selected.phone && (
                                    <a href={telHref(selected.phone)} className={`${primaryBtn} no-underline`}>
                                        <PhoneIcon className="w-4 h-4 inline mr-1" />{selected.phone}
                                    </a>
                                )}
                                {selected.domains && (
                                    <a href={`https://${selected.domains.split(',')[0].trim()}`} target="_blank" rel="noreferrer" className={secondaryBtn}>
                                        {selected.domains.split(',')[0].trim()} <ArrowTopRightOnSquareIcon className="w-3 h-3 inline" />
                                    </a>
                                )}
                            </div>

                            {selected.people?.length > 0 && (
                                <div className="text-sm">
                                    {selected.people.map((p) => (
                                        <div key={p.id} className="flex gap-2">
                                            <span className="font-semibold">{p.name}</span>
                                            {p.jobTitle && <span className="text-slate-400">{p.jobTitle.split(',').join(' · ')}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex rounded-lg overflow-hidden border border-slate-200">
                                <button type="button" onClick={() => setDetailTab('call')}
                                    className={`flex-1 px-3 py-2 font-mono text-xs font-bold uppercase transition-colors ${detailTab === 'call' ? 'bg-primary text-on-accent' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                                    <PhoneIcon className="w-4 h-4 inline mr-1" />Anruf
                                </button>
                                <button type="button" onClick={() => setDetailTab('teaser')}
                                    className={`flex-1 px-3 py-2 font-mono text-xs font-bold uppercase transition-colors ${detailTab === 'teaser' ? 'bg-primary text-on-accent' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                                    <PaperAirplaneIcon className="w-4 h-4 inline mr-1" />Teaser
                                    {prospectsByCrm[selected.id]?.searchProfile && (
                                        <span className="ml-1" title="Suchprofil vorhanden">●</span>
                                    )}
                                </button>
                            </div>

                            {detailTab === 'call' && (<>
                            {selected.description && (
                                <div className="text-xs text-slate-500 whitespace-pre-wrap max-h-28 overflow-y-auto border-l-2 border-slate-200 pl-2">
                                    {selected.description}
                                </div>
                            )}

                            <div>
                                <label className={labelClass}>Ergebnis</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {OUTCOME_ORDER.map((status) => (
                                        <button type="button" key={status}
                                            onClick={() => setPendingStatus((prev) => (prev === status ? '' : status))}
                                            className={`px-2 py-1 rounded-md font-mono text-[11px] font-bold uppercase border transition-all ${
                                                pendingStatus === status
                                                    ? 'border-primary ring-2 ring-primary ' + (STATUS_META[status]?.badge || '')
                                                    : 'border-slate-200 ' + (STATUS_META[status]?.badge || '')}`}>
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {pendingStatus === 'Termin' && !bookedMeeting && (
                                <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className={labelClass}>Profil-Check buchen (Mail geht sofort raus)</label>
                                        <button type="button" onClick={loadSlots} disabled={slotsLoading} title="Slots aktualisieren"
                                            className="text-slate-400 hover:text-slate-600">
                                            <ArrowPathIcon className={`w-3.5 h-3.5 ${slotsLoading ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                        {slots.slice(0, 16).map((slot) => (
                                            <button type="button" key={slot.start}
                                                onClick={() => setBookingSlot((prev) => (prev === slot.start ? '' : slot.start))}
                                                className={`px-2 py-1 rounded-md font-mono text-[11px] font-bold border transition-all ${
                                                    bookingSlot === slot.start
                                                        ? 'border-primary ring-2 ring-primary bg-white'
                                                        : 'border-slate-200 bg-white hover:border-primary'}`}>
                                                {fmtSlot(slot.start)}
                                            </button>
                                        ))}
                                        {slots.length === 0 && !slotsLoading && (
                                            <span className="text-xs text-slate-500">Keine freien Slots im Buchungsfenster.</span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input className={inputClass} value={bookingName} onChange={(e) => setBookingName(e.target.value)}
                                            placeholder="Ansprechpartner" />
                                        <input className={inputClass} type="email" value={bookingEmail} onChange={(e) => setBookingEmail(e.target.value)}
                                            placeholder="E-Mail (leer = keine Bestätigungs-Mail)" />
                                    </div>
                                    <button type="button" onClick={bookMeeting} className={`${primaryBtn} w-full`}
                                        disabled={booking || !bookingSlot}>
                                        {booking ? 'Bucht…' : bookingSlot ? `Buchen: ${fmtSlot(bookingSlot)}` : 'Slot wählen'}
                                    </button>
                                    <p className="text-[10px] text-slate-500 leading-snug">
                                        Bucht verbindlich: Bestätigungs-Mail an den Kunden (mit Video-Link, Kalender,
                                        Verschieben/Absagen), Benachrichtigung an David, CRM-Status „Termin&ldquo; + Wiedervorlage — alles automatisch.
                                    </p>
                                </div>
                            )}
                            {bookedMeeting && (
                                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                                    <div className="font-mono text-xs font-bold text-emerald-800 uppercase">Gebucht ✓</div>
                                    <div className="text-sm text-emerald-900 mt-1">
                                        {bookedMeeting.kindLabel}: {fmtSlot(bookedMeeting.startAt)} — Bestätigung ist raus.
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className={labelClass}>Notiz</label>
                                <textarea rows={3} className={inputClass} value={note} onChange={(e) => setNote(e.target.value)}
                                    placeholder="Was ist passiert? Wird mit Zeitstempel ins CRM-CallLog geschrieben…" />
                            </div>

                            <div>
                                <label className={labelClass}>Rückruf / Wiedervorlage</label>
                                <div className="flex gap-2">
                                    <input type="datetime-local" className={inputClass} value={followUpInput}
                                        onChange={(e) => setFollowUpInput(e.target.value)} />
                                    {selected.followUpAt && (
                                        <>
                                            <button type="button" title="Rückruf löschen" className={secondaryBtn}
                                                onClick={() => { setFollowUpInput(''); logCall({ clearFollowUp: true }); }}>
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                            <button type="button" title="Als ICS für den Kalender" className={secondaryBtn}
                                                onClick={() => downloadIcs(`rueckruf-${selected.name.replace(/\W+/g, '-')}.ics`, [selected])}>
                                                <ArrowDownTrayIcon className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <button type="button" onClick={() => logCall()} className={`${primaryBtn} w-full`}
                                disabled={saving || (!pendingStatus && !note.trim() && followUpInput === toDatetimeLocal(selected.followUpAt))}>
                                {saving ? 'Speichert…' : 'Ins CRM speichern'}
                            </button>

                            {selected.notes && (
                                <div>
                                    <label className={labelClass}>Call-Log</label>
                                    <div className="font-mono text-xs text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto bg-slate-50 rounded-lg p-2">
                                        {selected.notes}
                                    </div>
                                </div>
                            )}
                            </>)}

                            {detailTab === 'teaser' && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className={labelClass}>Teaser & Tender-Matching</label>
                                    {prospectsByCrm[selected.id] && (
                                        <span className="font-mono text-[10px] text-slate-400">{prospectsByCrm[selected.id].listName}</span>
                                    )}
                                </div>

                                {prospectsByCrm[selected.id]?.teaserToken && (
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                                        <span className="font-mono text-[10px] text-slate-400 uppercase shrink-0">hello</span>
                                        <a href={`https://hello.bidlyzer.com/${prospectsByCrm[selected.id].teaserToken}`}
                                            target="_blank" rel="noreferrer"
                                            className="font-mono text-[11px] text-primary truncate hover:underline flex-1">
                                            hello.bidlyzer.com/…
                                        </a>
                                        <span className="font-mono text-[12px] font-bold tracking-widest shrink-0" title="Code zum Vorlesen — der Kunde tippt ihn auf hello.bidlyzer.com ein">
                                            {formatHelloCode(prospectsByCrm[selected.id].helloCode)}
                                        </span>
                                        <button type="button" title="Link kopieren" className="text-slate-400 hover:text-slate-600 shrink-0"
                                            onClick={() => { navigator.clipboard?.writeText(`https://hello.bidlyzer.com/${prospectsByCrm[selected.id].teaserToken}`); setFeedback({ message: 'Link kopiert' }); }}>
                                            <ArrowDownTrayIcon className="w-3.5 h-3.5 rotate-180" />
                                        </button>
                                        <button type="button" onClick={sendHelloInvite} disabled={sendingHello}
                                            className="font-mono text-[10px] font-bold uppercase text-primary hover:underline disabled:opacity-50 shrink-0">
                                            {sendingHello ? 'Sendet…' : 'Mail senden'}
                                        </button>
                                    </div>
                                )}

                                {!prospectsByCrm[selected.id] ? (
                                    <button type="button" onClick={activateTeaser} disabled={activating} className={`${secondaryBtn} w-full`}>
                                        {activating ? 'Aktiviert…' : 'Für Teaser-Mails aktivieren (Suchprofil + Matching)'}
                                    </button>
                                ) : (
                                    <>
                                        <button type="button" onClick={generateProfile} disabled={generating}
                                            className={`${secondaryBtn} w-full`}
                                            title={prospectWebsite(prospectsByCrm[selected.id], selected)
                                                ? `Crawlt ${prospectWebsite(prospectsByCrm[selected.id], selected)} und leitet Suchbegriffe, CPV und NUTS ab (wie die TED-Query-Generierung)`
                                                : 'Keine Website bekannt — Domain im CRM pflegen'}>
                                            <SparklesIcon className={`w-4 h-4 inline mr-1 ${generating ? 'animate-pulse' : ''}`} />
                                            {generating ? 'Analysiere Website… (bis zu 2 Min)' : 'Profil aus Website generieren'}
                                        </button>

                                        <div className="space-y-2">
                                            <textarea rows={2} className={inputClass} value={profileForm.query}
                                                placeholder='Suchbegriffe (z.B. Vermessung OR "3D Laserscanning")'
                                                onChange={(e) => setProfileForm((f) => ({ ...f, query: e.target.value }))} />
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <input className={inputClass} value={profileForm.cpvCode} placeholder="CPV"
                                                        onChange={(e) => setProfileForm((f) => ({ ...f, cpvCode: e.target.value }))} />
                                                    <PresetChips presets={CPV_PRESETS} onPick={(v) => setProfileForm((f) => ({ ...f, cpvCode: v }))} />
                                                </div>
                                                <div>
                                                    <input className={inputClass} value={profileForm.nutsCodePrefix} placeholder="NUTS"
                                                        onChange={(e) => setProfileForm((f) => ({ ...f, nutsCodePrefix: e.target.value }))} />
                                                    <PresetChips presets={NUTS_PRESETS} onPick={(v) => setProfileForm((f) => ({ ...f, nutsCodePrefix: v }))} />
                                                </div>
                                            </div>
                                            {suggestion && (suggestion.cpvCodes?.length > 1 || suggestion.nutsCodes?.length > 1) && (
                                                <div className="flex flex-wrap items-center gap-1">
                                                    <span className="font-mono text-[10px] text-slate-400 uppercase">Alternativen:</span>
                                                    {(suggestion.cpvCodes || []).map((code) => (
                                                        <button key={`cpv-${code}`} type="button" title="Als CPV-Code übernehmen"
                                                            onClick={() => setProfileForm((f) => ({ ...f, cpvCode: code }))}
                                                            className={`px-1.5 py-0.5 rounded-md font-mono text-[10px] border transition-all ${profileForm.cpvCode === code ? 'border-primary text-primary' : 'border-slate-200 text-slate-500 hover:border-primary'}`}>
                                                            CPV {code}
                                                        </button>
                                                    ))}
                                                    {(suggestion.nutsCodes || []).map((code) => (
                                                        <button key={`nuts-${code}`} type="button" title="Als NUTS-Filter übernehmen"
                                                            onClick={() => setProfileForm((f) => ({ ...f, nutsCodePrefix: code }))}
                                                            className={`px-1.5 py-0.5 rounded-md font-mono text-[10px] border transition-all ${profileForm.nutsCodePrefix === code ? 'border-primary text-primary' : 'border-slate-200 text-slate-500 hover:border-primary'}`}>
                                                            NUTS {code}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            <button type="button" onClick={saveProfile} className={`${secondaryBtn} w-full`}>
                                                Profil speichern & neu suchen
                                            </button>
                                        </div>

                                        {matchesLoading && <div className="font-mono text-xs text-slate-400 uppercase">Suche Ausschreibungen…</div>}
                                        {matchesError && <div className="text-xs text-red-600">{matchesError}</div>}
                                        {!matchesLoading && matches.length > 0 && (
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-mono text-[10px] text-slate-400 uppercase">
                                                    {matches.length} Treffer · {checkedTenders.size} gewählt
                                                </span>
                                                <div className="flex-1" />
                                                {MATCH_LIMITS.map((n) => (
                                                    <button key={n} type="button" title={`Top ${n} laden`}
                                                        onClick={() => { setMatchLimit(n); loadMatches(prospectsByCrm[selected.id].id, selected.id, n); }}
                                                        className={`px-1.5 py-0.5 rounded-md font-mono text-[10px] font-bold border transition-all ${matchLimit === n ? 'border-primary text-primary' : 'border-slate-200 text-slate-500 hover:border-primary'}`}>
                                                        {n}
                                                    </button>
                                                ))}
                                                <button type="button" title="Alle auswählen"
                                                    onClick={() => setCheckedTenders(new Set(matches.map((t) => t.id)))}
                                                    className="px-1.5 py-0.5 rounded-md font-mono text-[10px] font-bold border border-slate-200 text-slate-500 hover:border-primary uppercase">
                                                    Alle
                                                </button>
                                                <button type="button" title="Auswahl leeren"
                                                    onClick={() => setCheckedTenders(new Set())}
                                                    className="px-1.5 py-0.5 rounded-md font-mono text-[10px] font-bold border border-slate-200 text-slate-500 hover:border-primary uppercase">
                                                    Keine
                                                </button>
                                            </div>
                                        )}
                                        {!matchesLoading && matches.length > 0 && (
                                            <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                                                {matches.map((tender) => (
                                                    <label key={tender.id} className="flex items-start gap-2 p-2 text-xs hover:bg-slate-50 cursor-pointer">
                                                        <input type="checkbox" checked={checkedTenders.has(tender.id)} onChange={() => toggleTender(tender.id)} className="mt-0.5" />
                                                        <span className="flex-1">
                                                            <span className="block font-medium">
                                                                {tender.title}
                                                                {tender.url && (
                                                                    <a href={tender.url} target="_blank" rel="noreferrer" title="Ausschreibung öffnen"
                                                                        onClick={(e) => e.stopPropagation()} className="ml-1 text-slate-400 hover:text-primary">
                                                                        <ArrowTopRightOnSquareIcon className="w-3 h-3 inline" />
                                                                    </a>
                                                                )}
                                                            </span>
                                                            <span className="font-mono text-[10px] text-slate-400">
                                                                {[tender.buyerName, tender.location?.city, tender.deadline && `bis ${formatCrmDateTime(tender.deadline).slice(0, 8)}`, formatValue(tender.estimatedValue, tender.currency)]
                                                                    .filter(Boolean).join(' · ')}
                                                                {tender.hasInsight && <span className="text-primary font-bold"> · INSIGHT</span>}
                                                            </span>
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        {!matchesLoading && !matchesError && matches.length === 0 && prospectsByCrm[selected.id].searchProfile && (
                                            <div className="font-mono text-xs text-slate-400 uppercase">Keine Treffer — Profil anpassen</div>
                                        )}

                                        <div className="space-y-2">
                                            <input className={inputClass} value={recipientDraft} placeholder="Empfänger-E-Mail (nach dem Anruf erfragt)"
                                                onChange={(e) => setRecipientDraft(e.target.value)} />
                                            <input className={inputClass} value={subjectDraft}
                                                placeholder='Betreff (leer = "N passende Ausschreibungen für …")'
                                                onChange={(e) => setSubjectDraft(e.target.value)} />
                                            <textarea rows={4} className={inputClass} value={personalNote}
                                                placeholder="Persönliche Notiz in der Mail (optional) — Zeilenumbrüche bleiben erhalten, https-Links werden klickbar"
                                                onChange={(e) => setPersonalNote(e.target.value)} />
                                            <div className="flex items-center gap-2">
                                                <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-slate-500">
                                                    <input type="checkbox" checked={includeInsight} onChange={(e) => setIncludeInsight(e.target.checked)} />
                                                    Insight-Box
                                                </label>
                                                <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-slate-500" title="Anrede und Textbausteine in Du-Form (#gernPerDu)">
                                                    <input type="checkbox" checked={informal} onChange={(e) => setInformal(e.target.checked)} />
                                                    Du-Form
                                                </label>
                                                <div className="flex-1" />
                                                <button type="button" onClick={() => sendTeaser()} className={primaryBtn}
                                                    disabled={sending || !recipientDraft.includes('@') || checkedTenders.size === 0}>
                                                    <PaperAirplaneIcon className="w-4 h-4 inline mr-1" />
                                                    {sending ? 'Sendet…' : `Teaser senden (${checkedTenders.size})`}
                                                </button>
                                            </div>
                                        </div>

                                        {prospectDetail?.emails?.length > 0 && (
                                            <div>
                                                <label className={labelClass}>Gesendete Mails</label>
                                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                                    {prospectDetail.emails.map((email) => (
                                                        <div key={email.id} className="font-mono text-[10px] text-slate-500">
                                                            {formatCrmDateTime(email.createdAt)} → {email.recipient} ({email.status}
                                                            {email.error ? `: ${email.error}` : ''})
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesCockpit;
